import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "./api";
import { queryKeys } from "./query-keys";

type Payload = Record<string, unknown>;

export function useCreateMenuItemMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: Payload) => api.createMenuItem(data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.menu.all }),
  });
}

export function useUpdateMenuItemMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: Payload }) => api.updateMenuItem(id, data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.menu.all }),
  });
}

export function useDeleteMenuItemMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => api.deleteMenuItem(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.menu.all }),
  });
}

async function invalidateMenuAndCategories(queryClient: ReturnType<typeof useQueryClient>) {
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: queryKeys.menu.all }),
    queryClient.invalidateQueries({ queryKey: queryKeys.categories.all }),
  ]);
}

export function useCreateCategoryMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: Payload) => api.createCategory(data),
    onSuccess: () => invalidateMenuAndCategories(queryClient),
  });
}

export function useUpdateCategoryMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: Payload }) => api.updateCategory(id, data),
    onSuccess: () => invalidateMenuAndCategories(queryClient),
  });
}

export function useDeleteCategoryMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => api.deleteCategory(id),
    onSuccess: () => invalidateMenuAndCategories(queryClient),
  });
}

export function useDeleteOrderMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => api.deleteOrder(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.orders.all }),
  });
}

export function useUpdateOrderStatusMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, status }: { id: number; status: string }) => api.updateOrderStatus(id, status),
    onMutate: async ({ id, status }) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.orders.all });
      const previousOrdersQueries = queryClient.getQueriesData({ queryKey: queryKeys.orders.all });

      queryClient.setQueriesData({ queryKey: queryKeys.orders.all }, (oldData: unknown) => {
        if (Array.isArray(oldData)) {
          return oldData.map((order: any) =>
            order?.id === id ? { ...order, status } : order
          );
        }

        if (oldData && typeof oldData === "object") {
          const order = oldData as any;
          if (order.id === id) {
            return { ...order, status };
          }
        }

        return oldData;
      });

      return { previousOrdersQueries };
    },
    onError: (_error, _variables, context) => {
      for (const [queryKey, queryData] of context?.previousOrdersQueries ?? []) {
        queryClient.setQueryData(queryKey, queryData);
      }
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.orders.all }),
  });
}
